import { Component, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { AhmedElshahatJobService } from '../../../../services/ahmed-elshahat-job';
import { JobApplicationService } from '../../../../services/job-application';

@Component({
  selector: 'app-myjobs',
  templateUrl: './myjobs.component.html',
  styleUrl: './myjobs.component.scss'
})
export class MyjobsComponent implements OnInit {
  jobs: any[] = [];
  loading = true;
  errorMessage = '';

  applicantsModalOpen = false;
  applicantsLoading = false;
  applicantsError = '';
  applicants: any[] = [];
  selectedApplicantsJob: any = null;

  private companyId: string | null = null;
  private userId: string | null = null;

  constructor(
    private jobService: AhmedElshahatJobService,
    private applicationService: JobApplicationService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.companyId = this.getStoredCompanyId();
    this.userId = this.getStoredUserId();
    this.loadJobs();
  }

  get isAr(): boolean {
    return this.transloco.getActiveLang() === 'ar';
  }

  loadJobs(): void {
    this.loading = true;
    this.errorMessage = '';

    const filters: any = {
      per_page: 100
    };

    if (this.companyId) {
      filters.company_id = this.companyId;
    } else if (this.userId) {
      filters.user_id = this.userId;
    }

    this.jobService.getJobs(filters).subscribe({
      next: (res: any) => {
        const allJobs = this.extractArray(res);

        this.jobs = this.companyId || this.userId
          ? allJobs.filter((job: any) => this.belongsToCurrentCompany(job))
          : allJobs;

        this.loading = false;
      },
      error: (err: any) => {
        console.log(err);
        this.errorMessage = err?.error?.message || 'تعذر تحميل الوظائف حاليا';
        this.loading = false;
      }
    });
  }

  getJobTitle(job: any): string {
    return this.isAr
      ? (job?.title_ar || job?.title_en || job?.title || 'وظيفة بدون عنوان')
      : (job?.title_en || job?.title_ar || job?.title || 'Untitled job');
  }

  getLocation(job: any): string {
    const city = this.isAr
      ? (job?.city?.city_name_ar || job?.city?.name_ar || job?.city_name_ar || job?.city_name)
      : (job?.city?.city_name_en || job?.city?.name_en || job?.city_name_en || job?.city_name);

    const country = this.isAr
      ? (job?.country?.country_name_ar || job?.country?.name_ar || job?.country_name_ar || job?.country_name)
      : (job?.country?.country_name_en || job?.country?.name_en || job?.country_name_en || job?.country_name);

    return [city, country].filter(Boolean).join('، ') || (this.isAr ? 'غير محدد' : 'Not specified');
  }

  getStatusLabel(status: string | null | undefined): string {
    const normalized = status || 'published';

    const labels: Record<string, { ar: string; en: string }> = {
      published: { ar: 'منشورة', en: 'Published' },
      draft: { ar: 'مسودة', en: 'Draft' },
      active: { ar: 'متاحة', en: 'Active' },
      inactive: { ar: 'غير نشطة', en: 'Inactive' },
      pending: { ar: 'قيد المراجعة', en: 'Pending' }
    };

    return labels[normalized]?.[this.isAr ? 'ar' : 'en'] || normalized;
  }

  getStatusClass(status: string | null | undefined): string {
    return `status-${status || 'published'}`;
  }

  getEmploymentTypeLabel(value: string | null | undefined): string {
    const normalized = value || '';

    const labels: Record<string, { ar: string; en: string }> = {
      full_time: { ar: 'دوام كامل', en: 'Full time' },
      part_time: { ar: 'دوام جزئي', en: 'Part time' },
      contract: { ar: 'عقد', en: 'Contract' },
      internship: { ar: 'تدريب', en: 'Internship' }
    };

    return labels[normalized]?.[this.isAr ? 'ar' : 'en'] || (this.isAr ? 'نوع غير محدد' : 'Type not set');
  }

  getWorkModelLabel(value: string | null | undefined): string {
    const normalized = value || '';

    const labels: Record<string, { ar: string; en: string }> = {
      on_site: { ar: 'من مقر العمل', en: 'On site' },
      remote: { ar: 'عن بعد', en: 'Remote' },
      hybrid: { ar: 'هجين', en: 'Hybrid' }
    };

    return labels[normalized]?.[this.isAr ? 'ar' : 'en'] || (this.isAr ? 'نظام غير محدد' : 'Model not set');
  }

  trackById(index: number, item: any): any {
    return item?.id || index;
  }

  trackByApplicationId(index: number, item: any): any {
    return item?.id || item?.user_id || index;
  }

  openApplicants(job: any): void {
    if (!job?.id) {
      return;
    }

    this.selectedApplicantsJob = job;
    this.applicantsModalOpen = true;
    this.applicantsLoading = true;
    this.applicantsError = '';
    this.applicants = [];

    this.applicationService.getApplicationsByJobId(job.id, { per_page: 100 }).subscribe({
      next: (res: any) => {
        this.applicants = this.extractArray(res);
        this.applicantsLoading = false;
      },
      error: () => {
        this.loadApplicantsFallback(job.id);
      }
    });
  }

  closeApplicants(): void {
    this.applicantsModalOpen = false;
    this.selectedApplicantsJob = null;
    this.applicants = [];
    this.applicantsError = '';
    this.applicantsLoading = false;
  }

  getApplicantName(application: any): string {
    const applicant = this.getApplicant(application);

    return (
      applicant?.name ||
      applicant?.full_name ||
      applicant?.username ||
      application?.name ||
      application?.full_name ||
      (this.isAr ? 'متقدم بدون اسم' : 'Unnamed applicant')
    );
  }

  getApplicantEmail(application: any): string {
    const applicant = this.getApplicant(application);
    return applicant?.email || application?.email || '';
  }

  getApplicantPhone(application: any): string {
    const applicant = this.getApplicant(application);
    return applicant?.phone || applicant?.phone_number || application?.phone || application?.phone_number || '';
  }

  getApplicantCv(application: any): string {
    const applicant = this.getApplicant(application);

    return (
      application?.cv_url ||
      application?.cv ||
      application?.user_cv ||
      applicant?.user_cv_url ||
      applicant?.user_cv ||
      applicant?.cv_url ||
      applicant?.cv ||
      ''
    );
  }

  getApplicationStatusLabel(status: string | null | undefined): string {
    const normalized = status || 'pending';

    const labels: Record<string, { ar: string; en: string }> = {
      pending: { ar: 'قيد المراجعة', en: 'Pending' },
      reviewed: { ar: 'تمت المراجعة', en: 'Reviewed' },
      shortlisted: { ar: 'في القائمة المختصرة', en: 'Shortlisted' },
      accepted: { ar: 'مقبول', en: 'Accepted' },
      rejected: { ar: 'مرفوض', en: 'Rejected' },
      cancelled: { ar: 'ملغي', en: 'Cancelled' }
    };

    return labels[normalized]?.[this.isAr ? 'ar' : 'en'] || normalized;
  }

  getApplicationStatusClass(status: string | null | undefined): string {
    return `application-status-${status || 'pending'}`;
  }

  private belongsToCurrentCompany(job: any): boolean {
    if (this.companyId) {
      const jobCompanyId = job?.company_id || job?.company?.id;
      return String(jobCompanyId) === String(this.companyId);
    }

    if (this.userId) {
      const jobUserId = job?.user_id || job?.user?.id;
      return String(jobUserId) === String(this.userId);
    }

    return true;
  }

  private loadApplicantsFallback(jobId: any): void {
    this.applicationService.getJobApplications({ job_id: jobId, per_page: 100 }).subscribe({
      next: (res: any) => {
        const allApplications = this.extractArray(res);

        this.applicants = allApplications.filter((application: any) => {
          const applicationJobId =
            application?.job_id ||
            application?.job?.id ||
            application?.ahmed_elshahat_job?.id;

          return String(applicationJobId) === String(jobId);
        });

        this.applicantsLoading = false;
      },
      error: (err: any) => {
        console.log(err);
        this.applicantsError =
          err?.error?.message ||
          (this.isAr ? 'تعذر تحميل المتقدمين حاليا' : 'Could not load applicants right now');
        this.applicantsLoading = false;
      }
    });
  }

  private getApplicant(application: any): any {
    return (
      application?.user ||
      application?.candidate ||
      application?.doctor ||
      application?.applicant ||
      application?.profile ||
      {}
    );
  }

  private extractArray(res: any): any[] {
    return res?.data?.data || res?.data || res || [];
  }

  private getStoredCompanyId(): string | null {
    if (!this.canUseLocalStorage()) {
      return null;
    }

    const directId =
      localStorage.getItem('company_id') ||
      localStorage.getItem('companyId');

    if (directId) {
      return directId;
    }

    const user = this.getStoredUser();
    return user?.company_id || user?.company?.id || user?.user?.company_id || null;
  }

  private getStoredUserId(): string | null {
    if (!this.canUseLocalStorage()) {
      return null;
    }

    const directId =
      localStorage.getItem('user_id') ||
      localStorage.getItem('userId');

    if (directId) {
      return directId;
    }

    const user = this.getStoredUser();
    return user?.id || user?.user_id || user?.user?.id || null;
  }

  private getStoredUser(): any {
    if (!this.canUseLocalStorage()) {
      return null;
    }

    const storedUser =
      localStorage.getItem('user') ||
      localStorage.getItem('currentUser') ||
      localStorage.getItem('auth_user') ||
      localStorage.getItem('userData') ||
      localStorage.getItem('user_data');

    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  }

  private canUseLocalStorage(): boolean {
    return typeof localStorage !== 'undefined';
  }

}
